import { Label, Modal, Spinner, Tooltip, Pagination, EmptyState, EmptyStateIcon, Title, EmptyStateVariant } from '@patternfly/react-core';
import { CogIcon, MinusCircleIcon, PenIcon, PlusCircleIcon, UserIcon, SearchIcon } from '@patternfly/react-icons';
import { Table, Tbody, Td, Th, Thead, Tr } from '@patternfly/react-table';
import React, { useEffect, useState } from 'react';
import { fetchTenantNames, fetchAuditsByEntity } from './FlowService';
import { TemplateData, AuditLogEntry, AuditPageable } from '../Views';
import styles from './AuditLog.module.css';

export function AuditLog({ 
  isModal,
  handleModalToggle,
  templateData,
}: {
  isModal: boolean;
  handleModalToggle: () => void;
  templateData?: TemplateData;
}) {
  return (
    <Modal
      width="75%"
      title="Audit Logs"
      isOpen={isModal}
      onClose={handleModalToggle}
      height="50%"
    >
      <TableBasic 
        templateData={templateData}
      />
    </Modal>
  );
}

export const TableBasic: React.FC<{
  templateData?: TemplateData;
}> = ({ templateData }) => {
  const [logs, setLogs] = useState<AuditLogEntry[]>([]);
  const [tenants, setTenants] = useState<Record<string,string>>({});
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState<number>(1);
  const [perPage, setPerPage] = useState<number>(10);
  const [totalItems, setTotalItems] = useState<number>(0);
  const defaultTenantID = "00000000-0000-0000-0000-000000000000";

  useEffect(() => {
    const fetchLogs = async () => {
      if (!templateData?.templateId) {
        setLoading(false);
        return;
      }

      setLoading(true);
      try {
        const pageable: AuditPageable = {
          page: Math.max(0, page - 1),
          size: perPage,
          sort: ['timestamp,desc']
        };

        const response = await fetchAuditsByEntity(templateData?.templateId, pageable);

        if (response?.data?.content) {
          setLogs(response.data.content);
          setTotalItems(response.data.totalElements);

          const tenantIds = Array.from(new Set(response.data.content.map((l) => l.tenantId)));
          if (tenantIds.length) {
            const headerTenantId = tenantIds[0];
            const names = await fetchTenantNames(tenantIds, headerTenantId);
            setTenants(names);
          }
        }
      } finally {
        setLoading(false);
      }
    };

    fetchLogs();
  }, [templateData?.templateId, page, perPage]);

  const extractMessage = (message: string) => {
    try {
      const jsonData = JSON.parse(message);
      return {
        templateName: jsonData.TN ?? '-',
        sourceEventType: jsonData.SET ?? '-',
        targetEventType: jsonData.TET ?? '-'
      };
    } catch (e) {
      return {
        templateName: '-',
        sourceEventType: '-',
        targetEventType: '-'
      };
    }
  };

  if (loading) {
    return <Spinner />;
  }

  if (!logs || logs.length === 0) {
    return (
      <EmptyState variant={EmptyStateVariant.small}>
        <EmptyStateIcon icon={SearchIcon} />
        <Title headingLevel="h2" size="lg">
          No audit logs found
        </Title>
      </EmptyState>
    );
  }

  return (
    <>
      <Table aria-label="Audit logs" variant="compact" borders>
        <Thead>
          <Tr>
            <Th className={styles.columnHeader}>Template Name</Th>
            <Th className={styles.columnHeader}>Source Event Type</Th>
            <Th className={styles.columnHeader}>Target Event Type</Th>
            <Th>Action</Th>
            <Th>User</Th>
            <Th>Tenant</Th>
            <Th>Timestamp</Th>
          </Tr>
        </Thead>
        <Tbody>
          {logs.map((l) => {
            const logInfo = extractMessage(l.message ?? '');
            return (
              <Tr key={l.id}>
                <Td dataLabel="Template Name" className={styles.cell}>
                  {logInfo.templateName}
                </Td>
                <Td dataLabel="Source Event Type" className={styles.cell}>
                  {logInfo.sourceEventType}
                </Td>
                <Td dataLabel="Target Event Type" className={styles.cell}>
                  {logInfo.targetEventType}
                </Td>
                <Td dataLabel="Action">
                  {l.actionType === "CREATED" ? (
                    <Label variant="outline" color="cyan" icon={<PlusCircleIcon />}>CREATED</Label>
                  ) : l.actionType === "UPDATED" ? (
                    <Label variant="outline" color="blue" icon={<PenIcon />}>UPDATED</Label>
                  ) : (
                    <Label variant="outline" color="red" icon={<MinusCircleIcon />}>DELETED</Label>
                  )}
                </Td>
                <Td dataLabel="User">
                  {l.actionBy
                    ? <Label color="cyan" icon={<UserIcon />}>{l.actionBy}</Label>
                    : <Label icon={<CogIcon />}>System</Label>}
                </Td>
                <Td dataLabel="Tenant">
                  <Tooltip content={<div>ID: {l.tenantId}</div>} position="top">
                    <span>
                      {l.tenantId === defaultTenantID 
                        ? "N/A"
                        : tenants[l.tenantId] || l.tenantId}
                    </span>
                  </Tooltip>
                </Td>
                <Td dataLabel="Timestamp">
                  {new Date(l.timestamp).toLocaleString()}
                </Td>
              </Tr>
            );
          })}
        </Tbody>
      </Table>
      <Pagination
        itemCount={totalItems}
        perPage={perPage}
        page={page}
        onSetPage={(_evt, newPage) => setPage(newPage)}
        onPerPageSelect={(_evt, newPerPage) => {
          setPerPage(newPerPage);
          setPage(1);
        }}
        perPageOptions={[
          { title: '10', value: 10 },
          { title: '20', value: 20 },
          { title: '50', value: 50 },
          { title: '100', value: 100 }
        ]}
      />
    </>
  );
};
